import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, UserPlus, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface Course {
  _id: string;
  title: string;
  students: { _id: string; name: string; email: string; }[];
}

const CourseAssignment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [students, setStudents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCourse();
    loadStudents();
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/courses/${id}`);
      if (response.data && response.data.course) {
        setCourse(response.data.course);
        setSelectedStudents(response.data.course.students?.map((s: any) => s._id) || []);
      } else {
        toast.error('Erreur lors du chargement du cours');
        navigate('/admin/courses');
      }
    } catch (error) {
      console.error('Load course error:', error);
      toast.error('Erreur lors du chargement du cours');
      navigate('/admin/courses');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      setLoadingStudents(true);
      const response = await api.get('/admin/users?role=student');
      if (response.data && response.data.users) {
        setStudents(response.data.users);
      } else {
        toast.error('Erreur lors du chargement des étudiants');
      }
    } catch (error) {
      console.error('Load students error:', error);
      toast.error('Erreur lors du chargement des étudiants');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentToggle = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAssignStudents = async () => {
    try {
      if (!course) return;

      // Get students to enroll (newly selected)
      const studentsToEnroll = selectedStudents.filter(
        studentId => !course.students.some(s => s._id === studentId)
      );

      // Get students to unenroll (previously enrolled but now deselected)
      const studentsToUnenroll = course.students
        .filter(s => !selectedStudents.includes(s._id))
        .map(s => s._id);

      if (studentsToEnroll.length > 0) {
        await api.post(`/admin/courses/${id}/enroll`, { studentIds: studentsToEnroll });
        toast.success(`${studentsToEnroll.length} étudiant(s) inscrit(s) au cours.`);
      }

      if (studentsToUnenroll.length > 0) {
        await api.post(`/admin/courses/${id}/unenroll`, { studentIds: studentsToUnenroll });
        toast.success(`${studentsToUnenroll.length} étudiant(s) désinscrit(s) du cours.`);
      }
      
      loadCourse(); // Reload course to update enrolled students list
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error('Erreur lors de l\'assignation des étudiants');
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || loadingStudents) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Cours non trouvé</h3>
          <button
            onClick={() => navigate('/admin/courses')}
            className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            Retour à la liste des cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/admin/courses/${id}`)}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Assigner des étudiants à "{course.title}"</h1>
          </div>
          <button
            onClick={handleAssignStudents}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Enregistrer les assignations
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Liste des étudiants</h2>
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Rechercher un étudiant par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
            />
            <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>

          {filteredStudents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Étudiant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigner
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map(student => (
                    <tr key={student._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-[#FFB639] rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {student.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-5 w-5 text-[#FFB639] rounded focus:ring-[#FFB639]"
                          checked={selectedStudents.includes(student._id)}
                          onChange={() => handleStudentToggle(student._id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucun étudiant trouvé.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CourseAssignment;
